import React, {useEffect, useState} from "react";
import ActionButton from "./Components/ActionButton";
import {CopyIcon, DeleteIcon, PreviewIcon} from "../../../../../../assests/GmsIcons";
import SectionHeader from "./Components/SectionHeader";
import DataTable from "./Components/DataTable";
import PopUp from "../PopUp/PopUp";
import CreateUIModel from "../PopUp/CreateUIModel";
import {TPaginationParams} from "../../../../Performance/analytics";
import {useModule} from "../../contexts/ModuleContext";
import {fetchUIModelsApi} from "../../services/ApiHelper";
import {UIModelAPI} from "../../types/api.types";
import {useUIModelApi} from "../../contexts/UIModelApiContext";
import Paginator from "../../../../../../Components/Common/PaginatorWithApi";
import DeleteProjectModal from "../../../Components/DeleteProjectModal";
import {formatDateWithTimeDDMMMYYYY} from "../../../../../../Common/HelperClass";
import Input from "../PopUp/Input";
import {toast} from "react-toastify";
import {TParty} from "../../../../../../types/Context/AppContext";

type UIModelTableProps = {
  id?: string;
  projectId: string;
  selectedParty: TParty;
}

const UIModelTable = ({id, projectId, selectedParty }: UIModelTableProps) => {
  const baseId = id || 'ui-model-table';
  const [paginationParams, setPaginationParams] = useState<TPaginationParams>({pageNumber: 1,pageSize: 10});
  const { loadExistingUIModel } = useUIModelApi();
  const [models, setModels] = useState<UIModelAPI[]>([]);
  const [showCreateUiModel, setShowCreateUiModel] = useState(false);
  const [copyUiModel, setCopyUiModel] = useState<UIModelAPI | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [deleteModal, setDeleteModal] = useState<UIModelAPI | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [sort, setSort] = useState<{sortBy: string, sortOrder: string}>({sortBy: "updatedAt", sortOrder: "desc"});
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const {setActiveView, hasEditPermission} = useModule();
  const {deleteUIModel} = useUIModelApi();


  function useDebounce(value: any, delay: any) {
    const [debouncedValue, setDebouncedValue] = useState(value);
      useEffect(() => {
        const handler = setTimeout(() => {
          setDebouncedValue(value);
        }, delay);

        return () => {
          clearTimeout(handler);
        };
      }, [value, delay]);

        return debouncedValue;
    }

    const fetchModels = async () => {
      setIsLoading(true);
      try {
        const responseData = await fetchUIModelsApi(projectId, paginationParams, sort.sortBy, sort.sortOrder, searchTerm);
        if (responseData?.done) {
          setTotalRecords(responseData.data.total ?? 10);
          setModels(responseData.data.models);
        }
      } catch (error) {
        console.error("Failed to fetch UI models:", error);
      } finally {
        setIsLoading(false);
      }
  };

  useEffect(() => {
    fetchModels();
  }, [projectId, paginationParams, sort]);

  useEffect(() => {
    fetchModels();
  }, [debouncedSearchTerm]);

  const handleCreateSuccess = () => {
    setShowCreateUiModel(false);
    fetchModels();
  };

  const columns = [
    {
      id: "name",
      header: "Name",
      width: "50%",
      sortEnabled: true,
      render: (model: UIModelAPI, index: number) => (
        <div className='tw-flex tw-items-center'>
          <div 
            onClick={() => {
              handleModelClick(model)}}
            className="tw-cursor-pointer tw-text-blue-600 hover:tw-text-blue-800 hover:tw-underline"
          >
            {model?.name ?? ''}
          </div>
          {model.is_promoted ? (
            <div
              key={index}
              className="tw-ms-3 tw-w-min tw-px-2 tw-py-1 tw-bg-gray-200 tw-text-gray-500 tw-rounded-full tw-text-xs"
            >
              {'Promoted'}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      id: "updatedAt",
      header: "Updated",
      width: "18%",
      sortEnabled: true,
      render: (model: UIModelAPI) => formatDateWithTimeDDMMMYYYY(model?.updatedAt ?? model?.createdAt)
    },
    {
      id: "preview",
      header: "Preview",
      width: "18%",
      render: (model: UIModelAPI, index: number) => (
        <ActionButton
          id="preview"
          rowIndex={index}
          onClick={() => {
            let selectedPartyName = (selectedParty!.name.replace(/ /g,"_")).toLowerCase();
            const rootPreviewURL: string = process.env.REACT_APP_ROOT_PREVIEW_URL ?? '';
            const previewUrl: string = `https://${selectedPartyName}.${rootPreviewURL}/preview?modelId=${projectId}&uiModelId=${model.id}`
            window.open(previewUrl, '_blank')!.focus();
          }}
          icon={<PreviewIcon />}
        />
      )
    },
    {
      id: "copy",
      header: "",
      render: (uiModel: UIModelAPI, index: number) => (
        <ActionButton
          id="copy"
          rowIndex={index}
          onClick={() => {
            if(!hasEditPermission()) {
              toast.error('You dont have permission to update this model')
              return;
            }
            setCopyUiModel(uiModel);
          }}
          icon={<CopyIcon />}
        />
      )
    },
    {
      id: "delete",
      header: "",
      render: (model: UIModelAPI, index: number) => {
        return hasEditPermission() ? (
          <ActionButton
            id="delete"
            rowIndex={index}
            onClick={() => {
                setDeleteModal(model);
            }}
            icon={<DeleteIcon />}
          />
        ) : <div/>;
      }
    }
  ];

  const handleModelClick = async (model: UIModelAPI) => {
    try {
      await loadExistingUIModel(model.id);
      setActiveView('editor');
    } catch (error) {
      console.error("Failed to load UI model:", error);
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setPaginationParams((prevState: TPaginationParams) => ({
      ...prevState,
      pageNumber: pageNumber,
    }));
  }

  const updatePageSize = (pageSize: number) => {
    setPaginationParams((prevState: TPaginationParams) => ({
      ...prevState,
      pageSize: pageSize,
      pageNumber: 1
    }));
  };

  const handleSort = async (column: string) => {
    if (sort.sortBy === column) {
      setSort({sortBy: column, sortOrder: sort.sortOrder === "asc" ? "desc" : "asc"})
    } else {
      setSort({sortBy: column, sortOrder: "asc"})
    }
  };

  return (
    <>
        <div id={`${baseId}-search-wrapper`} className='tw-w-[250px] tw-justify-self-end tw-mt-4'>
            <Input id={`${baseId}-search`} placeholder="Search..." value={searchTerm} error={null} onChangeValue={(e) => setSearchTerm(e)} disabled={false}/>
        </div>

      <SectionHeader
        id={`${baseId}-header`}
        title="UI Models"
        actionLabel="Create"
        onAction={() => {
          if(!hasEditPermission()) {
            toast.error('You dont have permission to update this model')
            return;
          }
          setShowCreateUiModel(true);
        }}
      />

      <DataTable<UIModelAPI>
        id={baseId}
        data={models}
        columns={columns}
        isLoading={isLoading}
        handleSort={handleSort}
        sortBy={sort.sortBy}
        sortOrder={sort.sortOrder}
      />

      <div id={`${baseId}-pagination-wrapper`} className="tw-flex tw-justify-end tw-mt-2">
        <div id={`${baseId}-page-size-wrapper`} className={`tw-flex tw-items-center tw-space-x-3`}>
          <span>Show</span>
          <select
              id={`${baseId}-page-size-select`}
              data-testid={`${baseId}-page-size-select`}
              className={`tw-border focus:tw-outline-none tw-p-1`}
              style={{width: "80px"}}
              aria-label="Records on page select"
              value={paginationParams?.pageSize ?? 10}
              onChange={(e) => {
                updatePageSize(parseInt(e?.target?.value));
              }}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
            <option value={25}>25</option>
          </select>
        </div>
        <Paginator
          onPageChange={handlePageChange}
          recordPerPage={paginationParams?.pageSize}
          presentPage={paginationParams?.pageNumber}
          totalRecords={totalRecords}
      />
      </div>

      {showCreateUiModel && (
        <PopUp
          id={`${baseId}-create-popup`}
          title="Create UI Model"
          content={
            <CreateUIModel
              id={`${baseId}-create-form`}
              projectId={projectId}
              onSuccess={handleCreateSuccess}
              onClose={() => setShowCreateUiModel(false)}
            />
          }
        />
      )}
        {copyUiModel != null && (
        <PopUp
          id={`${baseId}-copy-popup`}
          title="Copy UI Model"
          content={
            <CreateUIModel
              id={`${baseId}-copy-form`}
              uiModel={copyUiModel}
              projectId={projectId}
              onSuccess={handleCreateSuccess}
              onClose={() => setCopyUiModel(null)}
              isCopyModel={true}
            />
          }
        />
      )}
      <DeleteProjectModal
        showModal={deleteModal != null}
        closeModal={() => setDeleteModal(null)}
        loading={deleteLoading}
        onConfirm={async () => {
          setDeleteLoading(true)
            try {
              const responseData = await deleteUIModel(deleteModal?.id ?? '');
              if (responseData) {
                await fetchModels();
              }
              } catch (e: any) {
                toast.error(e.message)
              }
              setDeleteLoading(false)
              setDeleteModal(null)
          }}
          deleteItem={deleteModal}
          />
    </>
  );
};

export default UIModelTable;